import {
  GLOSSARY,
  glossarySlug,
  isGlossaryEntryOpen,
  searchGlossary,
  type GlossaryEntry,
} from '../game/glossaryData';
import { worldById } from '../models/world';
import { themeFor } from '../render/theme';
import { type ProgressMap } from '../services/progress';
import { escapeHtml } from './puzzleScreen';

export interface GlossaryView {
  readonly progress: ProgressMap;
  readonly focusSlug: string | null;
}

export function glossaryHash(term?: string): string {
  return term === undefined ? '#/glossary' : `#/glossary/${glossarySlug(term)}`;
}

export function unsealedCount(progress: ProgressMap): number {
  return GLOSSARY.filter((entry) => isGlossaryEntryOpen(progress, entry)).length;
}

export function glossaryMarkup(view: GlossaryView): string {
  const unsealed = unsealedCount(view.progress);
  return `
    <div class="screen screen--brief glossary">
      <header class="sub-header">
        <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
        <div>
          <h1>Engineer's Compendium</h1>
          <p data-role="count">${unsealed} of ${GLOSSARY.length} entries unsealed</p>
        </div>
      </header>
      <label class="field glossary__search">
        <span>Search the compendium</span>
        <input type="search" data-role="search" placeholder="Term, definition or field note"
               autocomplete="off" spellcheck="false" />
      </label>
      <section class="page" aria-label="Glossary entries">
        <ol class="page__list" data-role="list">
          ${GLOSSARY.map((entry) => entryMarkup(entry, view)).join('')}
        </ol>
        <p class="page__empty" data-role="empty" hidden>Nothing in the compendium matches that.</p>
      </section>
    </div>
  `;
}

function entryMarkup(entry: GlossaryEntry, view: GlossaryView): string {
  const world = worldById(entry.worldIndex);
  const accent = themeFor(world?.themeId).accent;
  const focused = entry.slug === view.focusSlug;
  const badge = `<span class="entry__badge" style="--world-accent: ${accent}" aria-label="District ${entry.worldIndex}">${entry.worldIndex}</span>`;
  if (!isGlossaryEntryOpen(view.progress, entry)) {
    return `
      <li class="entry entry--sealed${focused ? ' is-focused' : ''}" data-slug="${entry.slug}">
        <div class="entry__head">
          ${badge}
          <span class="entry__term">${escapeHtml(entry.term)}</span>
          <span class="entry__seal" aria-hidden="true"></span>
        </div>
        <p class="entry__sealed">Sealed. Earn a star in ${escapeHtml(world?.name ?? `District ${entry.worldIndex}`)} to break the wax.</p>
      </li>
    `;
  }
  return `
    <li class="entry${focused ? ' is-focused' : ''}" data-slug="${entry.slug}">
      <details class="entry__details"${focused ? ' open' : ''}>
        <summary class="entry__head">
          ${badge}
          <span class="entry__term">${escapeHtml(entry.term)}</span>
          <i class="entry__world">${escapeHtml(world?.name ?? '')}</i>
        </summary>
        <div class="entry__body">
          <p class="entry__definition">${escapeHtml(entry.definition)}</p>
          <p class="entry__lore">${escapeHtml(entry.lore)}</p>
        </div>
      </details>
    </li>
  `;
}

export function filterGlossaryList(root: ParentNode, query: string): void {
  const visible = new Set(searchGlossary(query).map((entry) => entry.slug));
  root.querySelectorAll<HTMLElement>('.entry').forEach((el) => {
    el.hidden = !visible.has(el.dataset['slug'] ?? '');
  });
  const empty = root.querySelector<HTMLElement>('[data-role="empty"]');
  if (empty) empty.hidden = visible.size > 0;
}
